// Shared maintenance-log drafts.
//
// A part-filled MNT-F-04 entry is held on the server, one per machine, so the whole
// unit can see what is in progress and any of them can finish it. Someone opens a
// breakdown, gets called away, and a colleague picks it up instead of retyping it.
//
// Drafts live in their own collection (log_drafts). Nothing in Reports, MTBF/MTTR or
// PM compliance reads them, so a half-typed entry can never move a figure.
//
// Because the draft is shared, two people can be in it at once. Every save carries the
// version it was based on; the server refuses a stale one rather than letting one
// person's save silently erase the other's typing.

import { apiCall } from "./api";

export async function listDrafts(unitId) {
  if (!unitId) return [];
  try {
    const data = await apiCall("GET", `/drafts/${unitId}`);
    return Array.isArray(data) ? data : [];
  } catch { return []; }          // the list is an aid, never a blocker
}

export async function getDraft(unitId, machineId) {
  if (!unitId || !machineId) return null;
  try {
    const d = await apiCall("GET", `/drafts/${unitId}/${machineId}`);
    return d && d.form ? d : null;
  } catch { return null; }
}

// Returns { ok, draft } or { ok: false, conflict, message } when someone else saved first.
export async function saveDraft(unitId, machineId, payload) {
  if (!unitId || !machineId) return { ok: false };
  try {
    const draft = await apiCall("PUT", `/drafts/${unitId}/${machineId}`, payload);
    return { ok: true, draft };
  } catch (e) {
    const status = e?.response?.status;
    return {
      ok: false,
      conflict: status === 409,
      message: e?.response?.data?.detail || "Could not save the draft.",
    };
  }
}

export async function deleteDraft(unitId, machineId) {
  if (!unitId || !machineId) return;
  try { await apiCall("DELETE", `/drafts/${unitId}/${machineId}`); } catch { /* best effort */ }
}

// Drafts used to be kept in this browser, one per user. They were never visible to
// anyone else and are superseded by the shared ones, so the old keys are cleared out.
export function purgeLocalDrafts() {
  try {
    const stale = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith("mnt.draft.")) stale.push(k);
    }
    stale.forEach(k => window.localStorage.removeItem(k));
  } catch { /* storage unavailable — nothing to clean */ }
}

export function formatDraftStamp(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

// "PARVEEN KUMAR · 2 min ago" for the drafts list.
export function draftWho(draft) {
  const by = draft?.updatedBy?.name || draft?.createdBy?.name || "";
  const started = draft?.createdBy?.name || "";
  if (by && started && by !== started) return `${started} started · ${by} last edited`;
  return by || started || "Unknown";
}
